import React from "react";
import {
  CircleCheck,
  CloudOff,
  AlertTriangle,
  RefreshCw,
  LogIn,
} from "lucide-react";
import { useProjectData } from "@/features/projects/ProjectDataContext";
import { useAuth } from "@/features/auth/AuthContext";
import { useTheme, cn } from "@/ui/theme/ThemeContext";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { useNoteSaveStatus } from '@/features/tasks/hooks/useNoteSaveStatus';

const OperationReviewDialog = React.lazy(() => import('./OperationReviewDialog').then(module => ({ default: module.OperationReviewDialog })));

export const SyncStatus: React.FC = () => {
  const { projectData, wsState, syncState, reconnectWebSocket, retrySyncRecovery, localCacheSaveFailed } = useProjectData();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { t } = useSettings();
  const noteStatus = useNoteSaveStatus();
  const [reviewOpen,setReviewOpen]=React.useState(false);

  const getStatusIcon = () => {
    if (!user) {
      return <LogIn className="w-4 h-4" />;
    }
    if (noteStatus === 'failed') return <AlertTriangle className="w-4 h-4" />;
    if (noteStatus === 'saving') return <RefreshCw className="w-4 h-4 animate-spin" />;
    if (noteStatus === 'pending') return <CloudOff className="w-4 h-4" />;
    if (localCacheSaveFailed) return <AlertTriangle className="w-4 h-4" />;

    if (wsState === "disconnected") {
      return <CloudOff className="w-4 h-4" />;
    }

    if (wsState === "connecting") {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }

    switch (syncState.syncStatus) {
      case "synced":
        return <CircleCheck className="w-4 h-4" />;
      case "syncing":
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case "pending":
        return <CloudOff className="w-4 h-4" />;
      case "conflict":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <CircleCheck className="w-4 h-4" />;
    }
  };

  const getStatusText = () => {
    if (!user) {
      return t("Login Required");
    }
    if (noteStatus === 'failed') return t('Failed to save');
    if (noteStatus === 'saving') return t('Syncing...');
    if (noteStatus === 'pending') return t('Unsaved changes');
    if (localCacheSaveFailed) return t('Local backup unavailable');

    if (wsState === "disconnected") {
      return t("Offline");
    }

    if (wsState === "connecting") {
      return t("Connecting...");
    }

    switch (syncState.syncStatus) {
      case "synced":
        return t("All changes saved");
      case "syncing":
        return t("Syncing...");
      case "pending":
        return t("{{count}} changes pending", { count: syncState.pendingOperations.length });
      case "conflict":
        return t("Sync conflict");
      default:
        return t("Connected");
    }
  };

  const getStatusColor = () => {
    if (!user) {
      return "text-yellow-700 dark:text-yellow-400";
    }
    if (noteStatus === 'failed') return 'text-red-600 dark:text-red-400';
    if (noteStatus === 'saving') return 'text-blue-600 dark:text-blue-400';
    if (noteStatus === 'pending') return 'text-orange-700 dark:text-orange-400';
    if (localCacheSaveFailed) return 'text-orange-700 dark:text-orange-400';

    if (wsState === "disconnected") {
      return "text-zinc-600 dark:text-gray-400";
    }

    if (wsState === "connecting") {
      return "text-blue-600 dark:text-blue-400";
    }

    switch (syncState.syncStatus) {
      case "synced":
        return "text-green-700 dark:text-green-400";
      case "syncing":
        return "text-blue-600 dark:text-blue-400";
      case "pending":
        return "text-orange-700 dark:text-orange-400";
      case "conflict":
        return "text-red-600 dark:text-red-400";
      default:
        return theme.textSecondary;
    }
  };

  const handleClick = () => {
    if (!user) return;

    if (wsState === "disconnected") {
      reconnectWebSocket();
    } else if (syncState.syncStatus === 'conflict') {
      setReviewOpen(true);
      void retrySyncRecovery?.().catch(() => {});
    }
  };

  return (
    <>
    <button
      type="button"
      aria-label={getStatusText()}
      data-testid="sync-status"
      className={cn(
        "relative inline-flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors",
        user && (wsState === "disconnected" || syncState.syncStatus === 'conflict') ? "cursor-pointer" : "cursor-default",
        theme.hover,
        getStatusColor()
      )}
      onClick={handleClick}
      title={
        !user
          ? t("Login Required")
          : getStatusText()
      }
    >
      {getStatusIcon()}
      {syncState.pendingOperations.length > 0 && (
        <span
          className={cn(
            "absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium",
            "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border border-orange-200 dark:border-orange-700"
          )}
        >
          {syncState.pendingOperations.length}
        </span>
      )}
    </button>
    {reviewOpen && user && <React.Suspense fallback={null}><OperationReviewDialog key={user.id} projectData={projectData} onClose={()=>setReviewOpen(false)} /></React.Suspense>}
    </>
  );
};
